/**
 * @fileoverview Syndicator Showdown — Game Prize Pool Resolution
 * @module utils/prizePool
 *
 * Resolves the GAME-economy prize pool for a race — the exact same
 * priority chain functions/src/results/settleRaceResult.js (real payouts)
 * and showdown-functions/value_engine_v1.js (expected-return maths) use:
 *
 *   race.adjustedPrizePool -> race.customPrizePool -> slate.defaultPrizePool
 *   -> slate.globalPrizePool -> (opt-in) split of race.racePrizemoney
 *   -> DEFAULT_PRIZE_POOL constant
 *
 * IMPORTANT: slate.defaultPrizePool here is the GAME's per-place virtual
 * prize map (what settleRaceResult.js actually pays winners via
 * calculateDividend) — not real racing prizemoney. Real race stakes
 * (race.racePrizemoney, a single total) are only used if the slate
 * explicitly opts in via useRealRaceStakesForReturns — never silently.
 * Keeping this identical to the settlement/value-engine priority means the
 * "Projected Payouts" preview in BuyModal never disagrees with what a
 * player will actually be paid when the race resolves.
 */

import { DEFAULT_PRIZE_POOL } from '@ss/shared/constants';

// Mirrors value_engine_v1.js's RACE_PRIZE_SPLIT_PCT — standard approximate
// stakes-race distribution, used only to split a single real racePrizemoney
// total into placings when a game opts into real stakes.
const RACE_PRIZE_SPLIT_PCT = [0.60, 0.20, 0.10, 0.06, 0.04];

function isUsablePool(pool) {
  return !!pool && Number(pool.first) > 0;
}

function splitRacePrizemoney(total) {
  const [p1, p2, p3, p4, p5] = RACE_PRIZE_SPLIT_PCT;
  return {
    first:  Math.round(total * p1),
    second: Math.round(total * p2),
    third:  Math.round(total * p3),
    fourth: Math.round(total * p4),
    fifth:  Math.round(total * p5),
  };
}

/**
 * @param {Object} race  - race doc: { adjustedPrizePool?, customPrizePool?, racePrizemoney? }
 * @param {Object} slate - slate doc: { defaultPrizePool?, globalPrizePool?, useRealRaceStakesForReturns? }
 * @returns {{ first, second, third, fourth, fifth }}
 */
export function resolveGamePrizePool(race = {}, slate = {}) {
  if (isUsablePool(race.adjustedPrizePool)) return race.adjustedPrizePool;
  if (isUsablePool(race.customPrizePool))   return race.customPrizePool;
  if (isUsablePool(slate.defaultPrizePool)) return slate.defaultPrizePool;
  if (isUsablePool(slate.globalPrizePool))  return slate.globalPrizePool;

  if (slate.useRealRaceStakesForReturns === true) {
    const total = Number(race.racePrizemoney);
    if (Number.isFinite(total) && total > 0) return splitRacePrizemoney(total);
  }

  return DEFAULT_PRIZE_POOL;
}
